import React, { useState } from 'react';
import { useTheme, useMediaQuery, Box, Typography, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';
import QrCodeIcon from '@/assets/icons/qr-code-icon.svg?react';
import PropTypes from 'prop-types';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useTranslate } from 'react-admin';

const StyledUserIdModal = styled(Box)(() => ({
    backgroundColor: Palette.white,
    padding: '24px',
    borderRadius: '16px',
    boxShadow: Palette.boxShadow.elevation16,
    fontSize: '11px',
    fontFamily: 'Inter, Arial, sans-serif !important',
    boxSizing: 'border-box',
    lineHeight: '16px',
    '& .content': {
        fontWeight: 600,
        marginTop: '4px',
    },
}));

const UserIdModal = ({ userId }) => {
    const translate = useTranslate();
    if (!userId) return null;

    return (
        <StyledUserIdModal>
            <Box className='title'>{translate('resources.transaction.fields.appCode')}</Box>
            <Box className='content'>{userId}</Box>
        </StyledUserIdModal>
    );
};

const UserIdPopper = ({ open, anchorEl, userId, onMouseEnter, onMouseLeave }) => {
    return (
        <Popper
            id={open ? 'user-id-popper' : undefined}
            open={open}
            anchorEl={anchorEl}
            placement='bottom'
            sx={{
                inset: '14px auto auto -16px !important',
                zIndex: 9999,
                '& ::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%) rotate(45deg)',
                    width: '16px',
                    height: '16px',
                    backgroundColor: Palette.white,
                },
                '& ::after': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: '-14px',
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'transparent',
                },
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <UserIdModal userId={userId} />
        </Popper>
    );
};

const TransactionAppColumnField = ({ record }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const translate = useTranslate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentElement, setCurrentElement] = useState(null);
    const userIdExists = record.userId && record.userId !== 'None';

    const handleMouseEnter = event => {
        if (!isDesktop || !record.userId) return;
        setAnchorEl(event.currentTarget);
        setCurrentElement(event.currentTarget);
    };

    const handleMouseLeave = () => {
        if (!isDesktop) return;
        setAnchorEl(null);
        setCurrentElement(null);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: userIdExists ? 'pointer' : 'default',
                '& svg': {
                    opacity: userIdExists ? 1 : 0.5,
                },
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <QrCodeIcon />
            <Typography variant='body1'>{userIdExists ? translate('common.boolean.yes') : translate('common.boolean.no')}</Typography>
            {isDesktop && userIdExists && anchorEl && (
                <UserIdPopper
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    userId={record.userId}
                    onMouseEnter={() => setAnchorEl(currentElement)}
                    onMouseLeave={handleMouseLeave}
                />
            )}
        </Box>
    );
};

TransactionAppColumnField.propTypes = {
    record: PropTypes.shape({
        userId: PropTypes.string,
    }).isRequired,
};

export default TransactionAppColumnField;
